/**
 * Typed encoders / decoders for `useDroppable` ids used across the
 * canvas explorer, canvas board, chat composer, and workspace surfaces.
 *
 * Wrapping every droppable's id behind these helpers means the
 * dispatcher only ever round-trips through one place, and there's a
 * single source of truth for the destination shape that
 * `classifyTransfer` consumes.
 */
import type { Entity } from "@cohesive/ui-components";
import type {
  ExplorerFileRecordCopyPayload,
  ExplorerSectionPayload,
} from "./payloads";
import type { FavoritesResourceType, TransferDestination } from "./types";

export type DroppableKind =
  | "workspace-folder"
  | "rail-workspace-folder"
  | "rail-favorites"
  | "canvas-scope"
  | "files-rail-canvas-scope"
  | "canvas-section"
  | "canvas-board"
  | "chat-composer"
  | "composer-attach";

/**
 * Stable id generators. Encoded ids are intentionally URL-ish so they
 * read clearly in React DevTools and in dnd-kit's debug output without
 * bringing JSON noise into element attributes.
 */
export const droppableId = {
  workspaceFolder(workspaceId: string, parentPath: string): string {
    return `ws-folder:${workspaceId}:${parentPath}`;
  },
  /** Home grid workspace tile — distinct from rail/detail `ws-folder` ids. */
  workspaceCard(workspaceId: string): string {
    return `workspace-card-drop:${workspaceId}`;
  },
  /** Files left rail — distinct from explorer ids when both are mounted. */
  railWorkspaceFolder(workspaceId: string, parentPath: string): string {
    return `rail-ws-folder:${workspaceId}:${parentPath}`;
  },
  /** Left-rail Favorites section — scoped per resource type. */
  railFavorites(resourceType: FavoritesResourceType): string {
    return `rail-favorites:${resourceType}`;
  },
  /** Left-rail chat folder — `null` folderId is the workspace root. */
  /** Workspace-detail-page chat folder card/row — distinct id from the rail's. */
  /**
   * Left-rail canvas row as a file drop target (add file into that canvas).
   * Distinct id from the canvas page's `canvas-scope` so both can be mounted.
   */
  railCanvasRow(canvasId: string): string {
    return `rail-canvas-row:${canvasId}`;
  },
  /**
   * Left-rail chat row as a file drop target (add file into that chat).
   * Distinct id from the chat page's `chat-composer` so both can be mounted.
   */
  railChatRow(flowSessionId: string): string {
    return `rail-chat-row:${flowSessionId}`;
  },
  canvasScope(canvasId: string): string {
    return `canvas-scope:${canvasId}`;
  },
  /** Files-rail "This canvas" context section — same destination as canvas-scope. */
  filesRailCanvasScope(canvasId: string): string {
    return `files-rail-canvas-scope:${canvasId}`;
  },
  canvasSection(canvasId: string, sectionId: string): string {
    return `canvas-section:${canvasId}:${sectionId}`;
  },
  canvasBoard(canvasId: string): string {
    return `canvas-board:${canvasId}`;
  },
  chatComposer(flowSessionId: string): string {
    return `chat-composer:${flowSessionId}`;
  },
  /**
   * Home/workspace composer (pending chat, no flow session yet). `scopeKey`
   * (workspace id or "home") only keeps ids distinct across surfaces.
   */
  composerAttach(scopeKey: string): string {
    return `composer-attach:${scopeKey}`;
  },
};

/**
 * Build the typed destination from a known `useDroppable` registration.
 * Mirrors the encoding above; returns the canonical destination shape
 * the classifier consumes so callers don't hand-construct it twice.
 */
export function destinationFor(
  kind: "workspace-folder",
  args: { workspaceId: string; parentPath: string },
): Extract<TransferDestination, { kind: "workspace" }>;
export function destinationFor(
  kind: "canvas-scope" | "canvas-board",
  args: { canvasId: string },
): Extract<TransferDestination, { kind: "canvas" }>;
export function destinationFor(
  kind: "canvas-section",
  args: { canvasId: string; sectionId: string },
): Extract<TransferDestination, { kind: "canvas_section" }>;
export function destinationFor(
  kind: "chat-composer",
  args: { flowSessionId: string },
): Extract<TransferDestination, { kind: "flow_session" }>;
export function destinationFor(
  kind: "composer-attach",
  args: { scopeKey: string },
): Extract<TransferDestination, { kind: "composer" }>;
export function destinationFor(
  kind: DroppableKind,
  args?:
    | { workspaceId: string; parentPath: string }
    | { canvasId: string }
    | { canvasId: string; sectionId: string }
    | { flowSessionId: string }
    | { scopeKey: string },
): TransferDestination {
  if (kind === "composer-attach") {
    const a = args as { scopeKey: string };
    return { kind: "composer", scopeKey: a.scopeKey };
  }
  if (kind === "workspace-folder" || kind === "rail-workspace-folder") {
    const a = args as { workspaceId: string; parentPath: string };
    return {
      kind: "workspace",
      workspaceId: a.workspaceId,
      parentPath: a.parentPath,
    };
  }
  if (kind === "canvas-section") {
    const a = args as { canvasId: string; sectionId: string };
    return {
      kind: "canvas_section",
      canvasId: a.canvasId,
      sectionId: a.sectionId,
    };
  }
  if (kind === "chat-composer") {
    const a = args as { flowSessionId: string };
    return {
      kind: "flow_session",
      flowSessionId: a.flowSessionId,
    };
  }
  const a = args as { canvasId: string };
  return { kind: "canvas", canvasId: a.canvasId };
}

/**
 * Marker the dispatcher checks to route canvas-board drops separately
 * from canvas-scope (pinned-list) drops. Both classify as
 * `kind: "canvas"` to the same canvas, but the board needs the cursor
 * coordinates / `screenToFlowPosition` plumbing while the scope is just
 * a pinned-list copy.
 */
export interface DroppableData {
  /** Discriminator for routing inside the dispatcher. */
  surface: DroppableKind;
  /** Canonical destination for `classifyTransfer`. */
  destination: TransferDestination;
  /**
   * Marks a droppable that floats OVER other droppables (e.g. the floating
   * chat window). Collision detection is smallest-rect-wins, so without this
   * a small target visually covered by the floating surface would still
   * steal the drop. When a hit has `occludes`, only hits inside its DOM
   * subtree survive that collision pass.
   */
  occludes?: boolean;
}

/**
 * Drag source data attached to every `useDraggable.data.current` so the
 * dispatcher can reconstruct the operation without prop-drilling
 * selection state through the row tree.
 */
export type DraggableData =
  | DraggableFilesData
  | DraggableSectionData
  | DraggableCanvasNodeData
  | DraggableChatSessionData
  | DraggableEntityData
  | DraggableCanvasCardData
  | DraggableIntegrationConnectionData
  | DraggableSkillData;

/**
 * One or more Entity objects dragged from a popover entity picker.
 */
export interface DraggableEntityData {
  kind: "entity";
  entities: Entity[];
  primaryName: string;
}

export interface DraggableFilesData {
  kind: "files";
  payloads: ExplorerFileRecordCopyPayload[];
  /** Canvases riding a mixed rail multi-drag — moved via the canvas API
   *  alongside the file moves on workspace destinations. */
  canvasIds?: string[];
  primaryName: string;
}

export interface DraggableSectionData {
  kind: "section";
  payload: ExplorerSectionPayload;
}

export interface DraggableCanvasNodeData {
  kind: "canvas-node";
  payload: {
    nodeId: string;
    nodeType: string;
    category: "entity" | "object";
    sourceCanvasId: string;
    currentParentSectionId: string | null;
    name: string;
  };
}

export interface DraggableChatSessionData {
  kind: "chat-session";
  payload: {
    flowSessionId: string;
    sourceCanvasId: string;
    name: string;
    /** Set when dragged from the chats rail so favorites drop can star via inbox API. */
    inboxItemId?: string;
    /** Workspace the chat lives in — root-drop no-ops validate against it. */
    sourceWorkspaceId?: string;
  };
}

export interface DraggableIntegrationConnectionData {
  kind: "integration-connection";
  connectionIds: string[];
  primaryName: string;
  /** Carried so the drag pill can render the connector's real icon. */
  platform?: string;
  imageURL?: string;
}

/** Skill row dragged from the skills left rail. */
export interface DraggableSkillData {
  kind: "skill";
  skillIds: string[];
  primaryName: string;
  /** Canvas board/section placement payload. */
  entities: Entity[];
}

/**
 * Canvas card(s) dragged from a workspace detail page or rail row.
 * Dropping onto a different workspace root reassigns `workspace_id`.
 */
export interface DraggableCanvasCardData {
  kind: "canvas-card";
  canvasIds: string[];
  sourceWorkspaceId: string;
  /** Folder provenance in the workspace tree, tri-state: a parent path
   *  ("Research/", known filed), `""` (known at the workspace root), or
   *  `undefined` (unknown — multi-canvas drags, and rows reached from
   *  Favorites/Shared/search, which carry a canvas but no tree row).
   *  Unknown must never be treated as "at root": a root drop with unknown
   *  provenance still runs the move (idempotent when redundant). */
  sourcePath?: string;
  primaryName: string;
  /** Canvas board/section placement payload — one canvas link entity per
   *  canvas, placed as a reference card (never a copy). */
  entities: Entity[];
}

export function asDroppableData(value: unknown): DroppableData | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Partial<DroppableData>;
  if (v.surface && v.destination) return v as DroppableData;
  return null;
}

export function asDraggableData(value: unknown): DraggableData | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;

  if (v.kind === "section") {
    const payload = v.payload as Partial<ExplorerSectionPayload> | undefined;
    if (!payload || typeof payload !== "object") return null;
    if (typeof payload.sectionId !== "string") return null;
    if (typeof payload.sectionName !== "string") return null;
    if (typeof payload.sourceCanvasId !== "string") return null;
    if (!Array.isArray(payload.files)) return null;
    return {
      kind: "section",
      payload: payload as ExplorerSectionPayload,
    };
  }

  if (v.kind === "chat-session") {
    const payload = v.payload as
      | Partial<DraggableChatSessionData["payload"]>
      | undefined;
    if (!payload || typeof payload !== "object") return null;
    if (typeof payload.flowSessionId !== "string") return null;
    if (typeof payload.sourceCanvasId !== "string") return null;
    if (typeof payload.name !== "string") return null;
    return {
      kind: "chat-session",
      payload: payload as DraggableChatSessionData["payload"],
    };
  }

  if (v.kind === "canvas-node") {
    const payload = v.payload as
      | Partial<DraggableCanvasNodeData["payload"]>
      | undefined;
    if (!payload || typeof payload !== "object") return null;
    if (typeof payload.nodeId !== "string") return null;
    if (typeof payload.nodeType !== "string") return null;
    if (payload.category !== "entity" && payload.category !== "object") {
      return null;
    }
    if (typeof payload.sourceCanvasId !== "string") return null;
    if (
      payload.currentParentSectionId !== null &&
      typeof payload.currentParentSectionId !== "string"
    ) {
      return null;
    }
    if (typeof payload.name !== "string") return null;
    return {
      kind: "canvas-node",
      payload: payload as DraggableCanvasNodeData["payload"],
    };
  }

  if (v.kind === "entity") {
    if (!Array.isArray(v.entities)) return null;
    if (typeof v.primaryName !== "string") return null;
    return {
      kind: "entity",
      entities: v.entities as Entity[],
      primaryName: v.primaryName,
    };
  }

  if (v.kind === "canvas-card") {
    if (!Array.isArray(v.canvasIds)) return null;
    if (!v.canvasIds.every((id) => typeof id === "string")) return null;
    if (typeof v.sourceWorkspaceId !== "string") return null;
    if (typeof v.primaryName !== "string") return null;
    if (!Array.isArray(v.entities)) return null;
    return {
      kind: "canvas-card",
      canvasIds: v.canvasIds as string[],
      sourceWorkspaceId: v.sourceWorkspaceId,
      sourcePath: typeof v.sourcePath === "string" ? v.sourcePath : undefined,
      primaryName: v.primaryName,
      entities: v.entities as Entity[],
    };
  }

  if (v.kind === "integration-connection") {
    if (!Array.isArray(v.connectionIds)) return null;
    if (!v.connectionIds.every((id) => typeof id === "string")) return null;
    if (typeof v.primaryName !== "string") return null;
    return {
      kind: "integration-connection",
      connectionIds: v.connectionIds as string[],
      primaryName: v.primaryName,
      platform: typeof v.platform === "string" ? v.platform : undefined,
      imageURL: typeof v.imageURL === "string" ? v.imageURL : undefined,
    };
  }

  if (v.kind === "skill") {
    if (!Array.isArray(v.skillIds)) return null;
    if (!v.skillIds.every((id) => typeof id === "string")) return null;
    if (typeof v.primaryName !== "string") return null;
    if (!Array.isArray(v.entities)) return null;
    return {
      kind: "skill",
      skillIds: v.skillIds as string[],
      primaryName: v.primaryName,
      entities: v.entities as Entity[],
    };
  }

  if (Array.isArray(v.payloads) && typeof v.primaryName === "string") {
    return {
      kind: "files",
      payloads: v.payloads as ExplorerFileRecordCopyPayload[],
      canvasIds: v.canvasIds as string[] | undefined,
      primaryName: v.primaryName,
    };
  }
  return null;
}
